//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { InputControl } from "@arangodb/ui";
import { useField, useFormikContext } from "formik";
import React from "react";
import { NewDeploymentFormValues } from "./newDeploymentForm.types";
import { useFetchLimits } from "./useFetchLimits";
import { useReinitializeNumberOfNodes } from "./useReinitializeNumberOfNodes";

export const NumberOfNodesInput = ({ isDisabled, isEditMode }: { isDisabled?: boolean; isEditMode?: boolean }) => {
  useReinitializeNumberOfNodes();
  const [modelIdField] = useField("modelId");
  const { values, initialValues } = useFormikContext<NewDeploymentFormValues>();
  const { data: limits } = useFetchLimits({ regionId: values.regionId });
  const { node_count } = limits || {};
  const { min = 1, max = 1 } = node_count || {};
  // In the public dashboard we don't allow scaling the number of DBServers down: when editing an
  // existing deployment the current node count is the floor. Reducing it is handled on demand via a ticket.
  const scaleDownFloor = isEditMode ? initialValues.numberOfNodes || 0 : 0;
  // The limits hook returns no node_count until the request resolves; until then min/max are placeholder
  // defaults (1) that don't reflect the deployment, so we must not validate the range against them.
  const areLimitsLoaded = !!node_count;
  // The current node count must always be a valid value (you can keep it or scale up), so the effective
  // bounds are widened to include it even if the regional limits report a smaller max.
  const minNodes = Math.max(min, scaleDownFloor);
  const maxNodes = Math.max(max, scaleDownFloor);
  // we ignore the value here as we just want to validate
  const [,] = useField({
    name: "numberOfNodes",
    validate: (value: number) => {
      // for developer & free model, number of nodes is always 1
      if (modelIdField.value === "developer" || modelIdField.value === "free") {
        if (value !== 1) {
          return `Number of nodes must be 1`;
        }
        return;
      }
      // The node count is only editable for sharded deployments; for any other model this input is
      // disabled and its value is irrelevant (it gets reinitialized on model change), so we skip the
      // scale-down floor / range checks to avoid blocking an allowed model switch (e.g. sharded -> flexible).
      if (modelIdField.value !== "sharded") {
        return;
      }
      if (value < scaleDownFloor) {
        return `Reducing the number of DBServers can only be done by creating a ticket requesting this. You must ensure the replication factors on all databases are set accordingly to handle the reduced number of DBServers.`;
      }
      if (areLimitsLoaded && (value < minNodes || value > maxNodes)) {
        return `Number of nodes must be between ${minNodes} and ${maxNodes}`;
      }
    },
  });

  return (
    <InputControl
      inputProps={{
        type: "number",
        // Note: the native min is kept at the API minimum (not the scale-down floor) so the browser's
        // native min-constraint popup doesn't overlay the validation message when a smaller value is typed.
        // The max is widened to the current node count so the native constraint can never invert (min > max).
        min: min,
        max: maxNodes,
        placeholder: "Node",
      }}
      isRequired
      isDisabled={modelIdField.value !== "sharded" || isDisabled}
      label="Number of nodes"
      name="numberOfNodes"
      tooltip="The number of nodes in the deployment. This can only be set for sharded deployments. For other types, the number of nodes is fixed."
    />
  );
};
